/**
 * Logical topology — a flat, Cisco-style diagram of the plant: active devices
 * in tiers (edge → security → core → access → services), one column per room.
 * Passive hops (patch panels, fiber shelves, rack-level link ends) collapse
 * into a per-room "cabling" bar, so a fiber run between two closets reads as
 * one backbone edge instead of a chain of panels.
 *
 * Pure: builds nodes, edges and coordinates; `LogicalView` only draws them.
 */
import { networkFootprint, rackTitle, ruLabel, type RackLinkRow, type RackPlant, type RackUnitRow } from './rackModel';

export const TIERS = ['Edge / WAN', 'Security', 'Core', 'Access', 'Services'] as const;
export type Tier = 0 | 1 | 2 | 3 | 4;

export type NodeIcon = 'router' | 'firewall' | 'gateway' | 'switch' | 'core' | 'server' | 'phone' | 'endpoint';

export interface TopoNode {
  id: string;                 // unit id, or `cab:<roomId>` / `pres:<roomId>`
  kind: 'device' | 'cabling' | 'presence';
  roomId: string;
  /** First unit of the node; grouped nodes list every unit in unitIds. */
  unitId: string | null;
  unitIds: string[];
  tier: Tier | null;          // null for cabling/presence
  icon: NodeIcon | null;
  label: string;
  sub: string;
  x: number; y: number; w: number; h: number;
}

export interface TopoEdge {
  id: string;
  from: string;
  to: string;
  links: RackLinkRow[];
}

export interface TopoColumn {
  roomId: string;
  title: string;
  x: number;
  w: number;
}

export interface TopoRow {
  label: string;
  y: number;
  h: number;
}

export interface Topology {
  nodes: TopoNode[];
  edges: TopoEdge[];
  columns: TopoColumn[];
  /** Each tier band that has a node, then the cabling band. */
  rows: TopoRow[];
  width: number;
  height: number;
  /** Devices left out by a network filter. */
  hidden: number;
}

const PASSIVE = new Set(['patch_panel', 'fiber', 'ups', 'pdu', 'vacant']);
const PASSIVE_LABEL = /surge|blank|cable manager/i;

export function isActive(u: RackUnitRow): boolean {
  return !PASSIVE.has(u.kind) && !PASSIVE_LABEL.test(u.label);
}

export function tierOf(u: RackUnitRow): Tier {
  const text = `${u.label} ${u.detail ?? ''}`;
  switch (u.kind) {
    case 'router': return 0;
    case 'firewall':
    case 'gateway': return 1;
    case 'switch': return /core/i.test(text) || u.ru_height >= 3 ? 2 : 3;
    default: return /controller|ctrl/i.test(text) ? 2 : 4;
  }
}

export function iconOf(u: RackUnitRow): NodeIcon {
  switch (u.kind) {
    case 'router': return 'router';
    case 'firewall': return 'firewall';
    case 'gateway': return 'gateway';
    case 'switch': return tierOf(u) === 2 ? 'core' : 'switch';
    case 'phone_system': return 'phone';
    case 'server':
    case 'shelf': return 'server';
    default: return /controller|ctrl/i.test(`${u.label} ${u.detail ?? ''}`) ? 'core' : 'endpoint';
  }
}

export const NODE_W = 132;
export const NODE_H = 76;
const NODE_GAP = 12;
const COL_PAD = 20;
const COL_GAP = 36;
const HEADER = 44;
const MAX_PER_LINE = 4;
const LINE_H = NODE_H + 18;
const TIER_GAP = 24;
const CABLING_H = 26;

export function buildTopology(plant: RackPlant, networkId?: string | null): Topology {
  const footprint = networkId ? networkFootprint(plant, networkId) : null;
  const keepUnit = (u: RackUnitRow) => isActive(u) && (!footprint || footprint.unitIds.has(u.id));
  const roomOfUnit = (u: RackUnitRow) => plant.rackById.get(u.rack_id)!.room_id;

  // Collapse each link end onto a node id.
  const endNode = (unitId: string | null, rackId: string): { id: string; roomId: string } => {
    const u = unitId ? plant.unitById.get(unitId) : undefined;
    const roomId = u ? roomOfUnit(u) : plant.rackById.get(rackId)!.room_id;
    return u && isActive(u) ? { id: u.id, roomId } : { id: `cab:${roomId}`, roomId };
  };

  const linkPool = footprint ? footprint.links : plant.links;
  const edgeMap = new Map<string, TopoEdge>();
  const cablingRooms = new Set<string>();
  for (const link of linkPool) {
    const fromUnit = plant.unitById.get(link.from_unit_id);
    if (!fromUnit) continue;
    const a = endNode(fromUnit.id, fromUnit.rack_id);
    const toRack = link.to_unit_id ? plant.unitById.get(link.to_unit_id)?.rack_id : link.to_rack_id;
    if (!toRack) continue;
    const b = endNode(link.to_unit_id, toRack);
    if (a.id === b.id) continue;
    // With a filter, a device end outside the network is not drawn.
    const visible = (n: { id: string }) => n.id.startsWith('cab:') || !footprint || footprint.unitIds.has(n.id);
    if (!visible(a) || !visible(b)) continue;
    if (a.id.startsWith('cab:')) cablingRooms.add(a.roomId);
    if (b.id.startsWith('cab:')) cablingRooms.add(b.roomId);
    const [p, q] = a.id < b.id ? [a.id, b.id] : [b.id, a.id];
    const key = `${p}|${q}`;
    const e = edgeMap.get(key) ?? { id: key, from: a.id, to: b.id, links: [] };
    e.links.push(link);
    edgeMap.set(key, e);
  }

  // Group devices by room and tier; identical devices in one room and tier
  // collapse into a single "×N" node (the five NEC chassis read as one).
  type Group = { key: string; units: RackUnitRow[] };
  const grid = new Map<string, Map<Tier, Group[]>>();
  let hidden = 0;
  for (const rack of plant.racks) {
    for (const u of plant.unitsByRack.get(rack.id) ?? []) {
      if (!isActive(u)) continue;
      if (!keepUnit(u)) { hidden++; continue; }
      const byTier = grid.get(rack.room_id) ?? new Map<Tier, Group[]>();
      const t = tierOf(u);
      const groups = byTier.get(t) ?? [];
      const key = `${t}|${u.label}`;
      const g = groups.find((x) => x.key === key);
      if (g) g.units.push(u);
      else groups.push({ key, units: [u] });
      byTier.set(t, groups);
      grid.set(rack.room_id, byTier);
    }
  }
  const unitToNode = new Map<string, string>();
  for (const byTier of grid.values()) {
    for (const groups of byTier.values()) for (const g of groups) for (const u of g.units) unitToNode.set(u.id, g.units[0].id);
  }

  const presenceRooms = new Set(
    footprint ? footprint.rooms.filter((r) => r.presenceOnly && r.units.length === 0).map((r) => r.roomId) : [],
  );
  const itemsIn = (roomId: string, t: Tier) =>
    (grid.get(roomId)?.get(t)?.length ?? 0) + (presenceRooms.has(roomId) && t === 0 ? 1 : 0);
  const usedTiers = ([0, 1, 2, 3, 4] as Tier[]).filter((t) => plant.rooms.some((r) => itemsIn(r.id, t) > 0));

  // Each tier is as tall as its most-wrapped column, so bands line up across rooms.
  const tierLines = new Map<Tier, number>(
    usedTiers.map((t) => [t, Math.max(1, ...plant.rooms.map((r) => Math.ceil(itemsIn(r.id, t) / MAX_PER_LINE)))]),
  );
  const rowY = new Map<Tier, number>();
  let yCursor = HEADER;
  for (const t of usedTiers) {
    rowY.set(t, yCursor);
    yCursor += tierLines.get(t)! * LINE_H + TIER_GAP;
  }
  const cablingY = yCursor + 4;

  const nodes: TopoNode[] = [];
  const columns: TopoColumn[] = [];
  let x = 0;
  for (const room of plant.rooms) {
    const byTier = grid.get(room.id);
    const hasCabling = cablingRooms.has(room.id);
    const hasPresence = presenceRooms.has(room.id);
    if (!byTier && !hasCabling && !hasPresence) continue;
    const across = Math.max(1, Math.min(MAX_PER_LINE, Math.max(...usedTiers.map((t) => itemsIn(room.id, t)), 1)));
    const w = across * NODE_W + (across - 1) * NODE_GAP + COL_PAD * 2;
    columns.push({ roomId: room.id, title: room.name ? `${room.code} · ${room.name}` : room.code, x, w });

    for (const t of usedTiers) {
      type Item = { kind: 'group'; g: Group } | { kind: 'presence' };
      const items: Item[] = (byTier?.get(t) ?? []).map((g) => ({ kind: 'group' as const, g }));
      if (hasPresence && t === 0) items.push({ kind: 'presence' });
      items.forEach((item, i) => {
        const line = Math.floor(i / MAX_PER_LINE);
        const onLine = Math.min(MAX_PER_LINE, items.length - line * MAX_PER_LINE);
        const lineW = onLine * NODE_W + (onLine - 1) * NODE_GAP;
        const nx = x + (w - lineW) / 2 + (i % MAX_PER_LINE) * (NODE_W + NODE_GAP);
        const ny = rowY.get(t)! + line * LINE_H;
        if (item.kind === 'presence') {
          nodes.push({
            id: `pres:${room.id}`, kind: 'presence', roomId: room.id, unitId: null, unitIds: [], tier: t, icon: null,
            label: 'Present here', sub: 'no device recorded', x: nx, y: ny, w: NODE_W, h: NODE_H,
          });
          return;
        }
        const first = item.g.units[0];
        const n = item.g.units.length;
        const rack = plant.rackById.get(first.rack_id)?.code;
        nodes.push({
          id: first.id, kind: 'device', roomId: room.id, unitId: first.id, unitIds: item.g.units.map((u) => u.id),
          tier: t, icon: iconOf(first),
          label: n > 1 ? `${first.label} ×${n}` : first.label,
          sub: n > 1 ? `${rack} · ${item.g.units.map((u) => `U${u.ru_start}`).join(' ')}` : `${rack} · ${ruLabel(first)}`,
          x: nx, y: ny, w: NODE_W, h: NODE_H,
        });
      });
    }
    if (hasCabling) {
      nodes.push({
        id: `cab:${room.id}`, kind: 'cabling', roomId: room.id, unitId: null, unitIds: [], tier: null, icon: null,
        label: `${room.code} cabling`, sub: 'patch / fiber',
        x: x + COL_PAD, y: cablingY, w: w - COL_PAD * 2, h: CABLING_H,
      });
    }
    x += w + COL_GAP;
  }

  // Map edge ends onto group nodes, and onto presence nodes for rack-level
  // network links; merge edges that now join the same pair.
  const merged = new Map<string, TopoEdge>();
  for (const e of edgeMap.values()) {
    const swap = (id: string) =>
      id.startsWith('cab:') ? (presenceRooms.has(id.slice(4)) ? `pres:${id.slice(4)}` : id) : unitToNode.get(id) ?? id;
    const from = swap(e.from);
    const to = swap(e.to);
    if (from === to) continue;
    const key = from < to ? `${from}|${to}` : `${to}|${from}`;
    const m = merged.get(key);
    if (m) m.links.push(...e.links);
    else merged.set(key, { id: key, from, to, links: [...e.links] });
  }
  const present = new Set(nodes.map((n) => n.id));
  const liveEdges = [...merged.values()].filter((e) => present.has(e.from) && present.has(e.to));
  // Drop cabling bars that no remaining edge touches.
  const touched = new Set(liveEdges.flatMap((e) => [e.from, e.to]));
  const finalNodes = nodes.filter((n) => n.kind !== 'cabling' || touched.has(n.id));
  const hasCablingRow = finalNodes.some((n) => n.kind === 'cabling');

  const rows: TopoRow[] = usedTiers.map((t) => ({ label: TIERS[t], y: rowY.get(t)!, h: tierLines.get(t)! * LINE_H }));
  if (hasCablingRow) rows.push({ label: 'Cabling', y: cablingY, h: CABLING_H });

  return {
    nodes: finalNodes,
    edges: liveEdges,
    columns,
    rows,
    width: Math.max(x - COL_GAP, 200),
    height: (hasCablingRow ? cablingY + CABLING_H : yCursor) + 12,
    hidden,
  };
}

export function nodeTitle(plant: RackPlant, node: TopoNode): string {
  if (node.unitId) {
    const u = plant.unitById.get(node.unitId)!;
    return `${u.label} · ${rackTitle(plant, u.rack_id)} ${ruLabel(u)}`;
  }
  return node.label;
}
